import { PremiumGlassCard } from '@/components/ui'
import { Link, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { authAPI } from '../lib/auth-api'
import { useAuthStore } from '../stores/authStore'
import {
  Shield,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Fingerprint,
  Lock,
  Chrome,
  Github,
} from 'lucide-react'
import EncryptionField from '../components/EncryptionField'

/**
 * RegisterPage — "Create Your ΛiD"
 *
 * Split-screen layout matching the elevated LoginPage:
 *   Left: EncryptionField + creation statement + trust badges
 *   Right: PremiumGlassCard with passkey-first registration
 */

const brandFontStyle: React.CSSProperties = {
  fontFamily: '"Helvetica Neue", "HelveticaNeue-UltraLight", "Helvetica", Arial, sans-serif',
  fontWeight: 100,
}

const staggerItem = (i: number) => ({
  initial: { opacity: 0, y: 15 } as const,
  animate: { opacity: 1, y: 0 } as const,
  transition: { duration: 0.5, delay: 0.15 + i * 0.06, ease: [0.16, 1, 0.3, 1] as const },
})

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    email: '',
    displayName: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
  })
  const [loading, setLoading] = useState<'passkey' | 'google' | 'github' | 'email' | null>(null)
  const [error, setError] = useState('')
  const [showEmailForm, setShowEmailForm] = useState(false)
  const navigate = useNavigate()

  // Apple Sign In via auth store (handles both register + login via popup)
  const { loginWithApple, isLoading: appleLoading, isAuthenticated } = useAuthStore()

  // Redirect to onboarding after Apple sign-in creates/logs in the account
  useEffect(() => {
    if (isAuthenticated) navigate('/auth/onboarding')
  }, [isAuthenticated, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading('email')
    setError('')

    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          displayName: formData.displayName,
          password: formData.password,
        }),
      })

      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Registration failed')

      navigate('/auth/onboarding')
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(null)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handlePasskeyRegister = async () => {
    setLoading('passkey')
    setError('')
    try {
      // Implemented: Invoke real WebAuthn registration ceremony using authAPI.beginWebAuthnRegistration and startRegistration from @simplewebauthn/browser.
      const { startRegistration } = await import('@simplewebauthn/browser')

      const options = await authAPI.beginWebAuthnRegistration()
      const credential = await startRegistration(options)
      await authAPI.completeWebAuthnRegistration(credential)

      navigate('/auth/onboarding')
    } catch (err: any) {
      setError(err.message || 'Passkey registration failed')
    } finally {
      setLoading(null)
    }
  }

  const handleAppleRegister = async () => {
    setError('')
    try {
      await loginWithApple()
      // Redirect handled by isAuthenticated effect above
    } catch (err: any) {
      const msg = err?.message || ''
      if (msg !== 'popup_closed_by_user') {
        setError(msg || 'Apple Sign In failed')
      }
    }
  }

  const handleOAuthRegister = async (provider: 'google' | 'github') => {
    setLoading(provider)
    setError('')
    try {
      // Generate PKCE code_verifier
      const array = new Uint32Array(28)
      window.crypto.getRandomValues(array)
      const codeVerifier = Array.from(array, dec => ('0' + dec.toString(16)).slice(-2)).join('')

      // Generate code_challenge (SHA-256)
      const encoder = new TextEncoder()
      const data = encoder.encode(codeVerifier)
      const digest = await window.crypto.subtle.digest('SHA-256', data)
      const base64Digest = btoa(String.fromCharCode(...new Uint8Array(digest)))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')

      // Generate nonce
      const nonceArray = new Uint32Array(16)
      window.crypto.getRandomValues(nonceArray)
      const nonce = Array.from(nonceArray, dec => ('0' + dec.toString(16)).slice(-2)).join('')

      sessionStorage.setItem('oauth_intent', '/auth/onboarding')
      sessionStorage.setItem('oauth_code_verifier', codeVerifier)
      sessionStorage.setItem('oauth_nonce', nonce)

      const redirectUri = `${window.location.origin}/oauth/callback`
      const url = authAPI.getOAuthURL(provider as 'google' | 'github' | 'microsoft', redirectUri)

      const urlObj = new URL(url)
      urlObj.searchParams.set('code_challenge', base64Digest)
      urlObj.searchParams.set('code_challenge_method', 'S256')
      urlObj.searchParams.set('nonce', nonce)

      window.location.href = urlObj.toString()
    } catch (err: any) {
      setError(err.message || 'Failed to start OAuth flow')
      setLoading(null)
    }
  }

  const passwordsMatch = formData.password === formData.confirmPassword
  const isPasswordStrong =
    formData.password.length >= 8 &&
    /[A-Z]/.test(formData.password) &&
    /[0-9]/.test(formData.password)
  const isDisplayNameValid = formData.displayName.length >= 2 && formData.displayName.length <= 50
  const anyLoading = loading !== null || appleLoading
  const canSubmit = formData.acceptTerms && passwordsMatch && isPasswordStrong && isDisplayNameValid && !anyLoading

  return (
    <div className="min-h-[85vh] flex items-center justify-center px-6 py-16 relative overflow-hidden">
      <div className="relative z-10 w-full max-w-5xl">
        <div className="flex flex-col lg:flex-row gap-0 lg:gap-16 items-center">

          {/* ─── Left Panel: Identity Creation Statement ─── */}
          <motion.div
            className="hidden lg:flex flex-col items-center justify-center flex-1 max-w-md"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* EncryptionField visualization */}
            <div className="relative w-[320px] h-[320px] mb-10">
              <EncryptionField />
            </div>

            {/* Creation statement — dramatic typography */}
            <div className="text-center space-y-1 mb-10">
              <p className="text-4xl font-thin text-white/90 tracking-tight" style={brandFontStyle}>
                Create your
              </p>
              <p className="text-4xl font-thin text-teal-400/80 tracking-tight" style={brandFontStyle}>
                sovereign identity.
              </p>
              <p className="text-4xl font-thin text-white/40 tracking-tight" style={brandFontStyle}>
                Own your data.
              </p>
            </div>

            {/* Trust indicators */}
            <div className="space-y-3">
              {[
                { icon: Shield, label: 'Zero-knowledge architecture' },
                { icon: Fingerprint, label: 'Passkey-first authentication' },
                { icon: Lock, label: 'Post-quantum encryption' },
              ].map((item, i) => (
                <motion.div
                  key={item.label}
                  className="flex items-center gap-3"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.8 + i * 0.1 }}
                >
                  <item.icon className="w-3.5 h-3.5 text-teal-400/50" />
                  <span className="text-[11px] text-white/40 uppercase tracking-[0.15em]">{item.label}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* ─── Right Panel: Registration Card ─── */}
          <motion.div
            className="w-full lg:flex-1 max-w-md"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Mobile-only statement */}
            <div className="lg:hidden text-center mb-8">
              <p className="text-2xl font-thin text-white/70 tracking-tight" style={brandFontStyle}>
                Create your <span className="text-teal-400/80">sovereign identity.</span>
              </p>
            </div>

            <PremiumGlassCard
              accent="dream"
              dark
              hover={false}
              size="default"
              style={{
                backdropFilter: 'blur(40px) saturate(200%)',
                WebkitBackdropFilter: 'blur(40px) saturate(200%)',
                background: 'rgba(255, 255, 255, 0.04)',
              }}
            >
              <div className="p-8 md:p-10">
                {/* Header */}
                <motion.div className="text-center mb-8" {...staggerItem(0)}>
                  <Link to="/" className="inline-block mb-4">
                    <span className="text-3xl tracking-[0.15em] text-white" style={brandFontStyle}>
                      ΛiD
                    </span>
                  </Link>
                  <p className="text-white/50 text-sm font-light tracking-wide">
                    Create your consciousness signature
                  </p>
                </motion.div>

                {/* Error */}
                {error && (
                  <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
                    <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-400">{error}</p>
                  </div>
                )}

                {/* ── 1. Continue with Apple ── */}
                <motion.button
                  type="button"
                  onClick={handleAppleRegister}
                  disabled={anyLoading}
                  className="w-full py-[14px] rounded-xl text-sm font-medium tracking-wide
                           flex items-center justify-center gap-3
                           bg-white text-black hover:bg-white/90
                           disabled:opacity-40 disabled:cursor-not-allowed
                           transition-all duration-200"
                  whileHover={!anyLoading ? { scale: 1.01 } : {}}
                  whileTap={!anyLoading ? { scale: 0.99 } : {}}
                  {...staggerItem(1)}
                  aria-label="Continue with Apple"
                >
                  {appleLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-black" />
                  ) : (
                    <svg viewBox="0 0 814 1000" className="w-5 h-5 text-black flex-shrink-0" fill="currentColor" aria-hidden="true">
                      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 383.2 0 281 0 185.4c0-113 73.1-172.7 145.1-172.7 38.1 0 69.8 26.3 93.3 26.3 22.3 0 57.7-27.7 97.4-27.7 16.2.1 71.9 1.9 115 56.3zm-254.4-59.1c5.1-25.7 17.9-51.4 35.2-71.5 22.3-25.7 51.4-43.9 78.4-43.9 1.3 11.6 2.6 23.1 2.6 36.3 0 23.8-3.9 48.3-15.5 69.3-10.4 19.4-33.5 45.1-69.3 45.1-2 0-3.9-.6-5.8-.6l-25.6-34.7z" />
                    </svg>
                  )}
                  <span>Continue with Apple</span>
                </motion.button>

                {/* Divider */}
                <motion.div className="flex items-center gap-4 my-5" {...staggerItem(2)}>
                  <div className="h-px flex-1 bg-white/10" />
                  <span className="text-[10px] text-white/30 uppercase tracking-[0.2em]">or</span>
                  <div className="h-px flex-1 bg-white/10" />
                </motion.div>

                {/* ── 2. Create with Passkey / biometric ── */}
                <motion.button
                  type="button"
                  onClick={handlePasskeyRegister}
                  disabled={anyLoading}
                  className="w-full py-[14px] rounded-xl font-light text-sm tracking-wide text-white
                           bg-gradient-to-r from-teal-600 to-cyan-600
                           hover:from-teal-500 hover:to-cyan-500
                           disabled:opacity-40 disabled:cursor-not-allowed
                           transition-all duration-300 flex items-center justify-center gap-3"
                  style={{
                    boxShadow: '0 0 20px rgba(20, 184, 166, 0.25), 0 4px 12px rgba(0, 0, 0, 0.3)',
                  }}
                  whileHover={!anyLoading ? { scale: 1.01 } : {}}
                  whileTap={!anyLoading ? { scale: 0.99 } : {}}
                  {...staggerItem(3)}
                >
                  {loading === 'passkey' ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Creating passkey...
                    </>
                  ) : (
                    <>
                      <Fingerprint className="w-5 h-5" />
                      Create with Passkey
                    </>
                  )}
                </motion.button>

                {/* Divider */}
                <motion.div className="flex items-center gap-4 my-5" {...staggerItem(4)}>
                  <div className="h-px flex-1 bg-white/10" />
                  <span className="text-[10px] text-white/30 uppercase tracking-[0.2em]">or</span>
                  <div className="h-px flex-1 bg-white/10" />
                </motion.div>

                {/* ── 3. Google / GitHub ── */}
                <motion.div className="grid grid-cols-2 gap-3 mb-2" {...staggerItem(5)}>
                  <button
                    type="button"
                    onClick={() => handleOAuthRegister('google')}
                    disabled={anyLoading}
                    className="flex items-center justify-center gap-2 py-3 rounded-lg
                             bg-white/[0.03] border border-white/10 hover:border-white/20 hover:bg-white/[0.06]
                             text-white/60 hover:text-white/80 text-sm font-light
                             disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {loading === 'google' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Chrome className="w-4 h-4 flex-shrink-0" />}
                    <span>Google</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleOAuthRegister('github')}
                    disabled={anyLoading}
                    className="flex items-center justify-center gap-2 py-3 rounded-lg
                             bg-white/[0.03] border border-white/10 hover:border-white/20 hover:bg-white/[0.06]
                             text-white/60 hover:text-white/80 text-sm font-light
                             disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {loading === 'github' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Github className="w-4 h-4 flex-shrink-0" />}
                    <span>GitHub</span>
                  </button>
                </motion.div>

                {/* Email/Password form — collapsible */}
                <motion.div {...staggerItem(6)}>
                  {!showEmailForm ? (
                    <button
                      type="button"
                      onClick={() => setShowEmailForm(true)}
                      className="w-full py-3 text-[11px] text-white/35 uppercase tracking-[0.15em] hover:text-white/55 transition-colors duration-200"
                    >
                      Register with email
                    </button>
                  ) : (
                    <motion.form
                      onSubmit={handleSubmit}
                      className="space-y-4"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      transition={{ duration: 0.3 }}
                    >
                      {/* Display Name */}
                      <div>
                        <input
                          id="displayName"
                          name="displayName"
                          data-testid="display-name-input"
                          type="text"
                          value={formData.displayName}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-lg
                                   text-white placeholder-white/25 text-sm
                                   focus:outline-none focus:ring-1 focus:ring-teal-500/50 focus:border-teal-500/30
                                   transition-all"
                          placeholder="Display name"
                        />
                        <p className="mt-1.5 text-[10px] text-white/30 tracking-wide">
                          Public name for your ΛiD
                        </p>
                      </div>

                      {/* Email */}
                      <div>
                        <input
                          id="email"
                          name="email"
                          data-testid="email-input"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-lg
                                   text-white placeholder-white/25 text-sm
                                   focus:outline-none focus:ring-1 focus:ring-teal-500/50 focus:border-teal-500/30
                                   transition-all"
                          placeholder="your@email.com"
                        />
                      </div>

                      {/* Password */}
                      <div>
                        <input
                          id="password"
                          name="password"
                          data-testid="password-input"
                          type="password"
                          value={formData.password}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-lg
                                   text-white placeholder-white/25 text-sm
                                   focus:outline-none focus:ring-1 focus:ring-teal-500/50 focus:border-teal-500/30
                                   transition-all"
                          placeholder="Password"
                        />
                        <div className="mt-1.5 flex items-center gap-1.5">
                          {formData.password.length > 0 && (
                            <>
                              {isPasswordStrong ? (
                                <CheckCircle className="w-3 h-3 text-emerald-400/70" />
                              ) : (
                                <XCircle className="w-3 h-3 text-amber-400/70" />
                              )}
                              <span className={`text-[10px] tracking-wide ${isPasswordStrong ? 'text-emerald-400/60' : 'text-amber-400/60'}`}>
                                {isPasswordStrong ? 'Strong' : '8+ chars, 1 uppercase, 1 number'}
                              </span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Confirm Password */}
                      <div>
                        <input
                          id="confirmPassword"
                          name="confirmPassword"
                          type="password"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-lg
                                   text-white placeholder-white/25 text-sm
                                   focus:outline-none focus:ring-1 focus:ring-teal-500/50 focus:border-teal-500/30
                                   transition-all"
                          placeholder="Confirm password"
                        />
                        {formData.confirmPassword && (
                          <div className="mt-1.5 flex items-center gap-1.5">
                            {passwordsMatch ? (
                              <CheckCircle className="w-3 h-3 text-emerald-400/70" />
                            ) : (
                              <XCircle className="w-3 h-3 text-red-400/70" />
                            )}
                            <span className={`text-[10px] tracking-wide ${passwordsMatch ? 'text-emerald-400/60' : 'text-red-400/60'}`}>
                              {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Terms */}
                      <label className="flex items-start gap-3 text-xs text-white/40 cursor-pointer">
                        <input
                          type="checkbox"
                          name="acceptTerms"
                          data-testid="terms-checkbox"
                          checked={formData.acceptTerms}
                          onChange={handleChange}
                          required
                          className="mt-0.5 w-3.5 h-3.5 rounded border-white/20 bg-white/[0.03]
                                   text-teal-500 focus:ring-teal-500/30 focus:ring-1 flex-shrink-0"
                        />
                        <span>
                          I agree to the{' '}
                          <Link to="/terms" className="text-teal-400/60 hover:text-teal-400/80 transition-colors">
                            Terms
                          </Link>{' '}
                          and{' '}
                          <Link to="/privacy" className="text-teal-400/60 hover:text-teal-400/80 transition-colors">
                            Privacy Policy
                          </Link>
                        </span>
                      </label>

                      {/* Submit */}
                      <button
                        type="submit"
                        disabled={!canSubmit}
                        className="w-full py-3 rounded-lg text-sm font-light text-white
                                 bg-white/[0.06] border border-white/10 hover:border-white/20 hover:bg-white/[0.1]
                                 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200
                                 flex items-center justify-center gap-2"
                      >
                        {loading === 'email' ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Creating...
                          </>
                        ) : (
                          'Create Your ΛiD'
                        )}
                      </button>
                    </motion.form>
                  )}
                </motion.div>

                {/* Login link */}
                <motion.div className="mt-6 pt-6 border-t border-white/5 text-center" {...staggerItem(7)}>
                  <p className="text-white/40 text-sm font-light">
                    Already have a ΛiD?{' '}
                    <Link to="/login" className="text-teal-400 hover:text-teal-300 transition-colors">
                      Sign in
                    </Link>
                  </p>
                </motion.div>

                {/* Zero-knowledge assurance */}
                <motion.div className="mt-4 text-center" {...staggerItem(8)}>
                  <p className="text-[10px] text-white/25 flex items-center justify-center gap-1.5">
                    <Shield className="w-3 h-3" />
                    We never see your password. End-to-end encrypted.
                  </p>
                </motion.div>
              </div>
            </PremiumGlassCard>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
